import {
  forwardRef, useCallback, useEffect, useState,
} from 'react';
import { map } from 'lodash';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { CardMedia, MobileStepper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getImageMediumUrl } from '@sda/utils/image';
import { getImageArrayFromField } from '@sda/utils/image/getImageArrayFromField';
import { ImageLoader } from './common/ImageLoader';
import { ArrowButton } from './ArrowButton';
import type { MediaProps } from './interfaces';

const useStyles = makeStyles({ name: 'Media' })((theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    zIndex: 0,
    userSelect: 'none',
  },
  embla: {
    overflow: 'hidden',
  },
  emblaContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  emblaSlide: {
    flex: '0 0 100%',
    minWidth: '0',
    position: 'relative',
  },
  preserveRatio: {
    aspectRatio: '16 / 9',

    '&::before': {
      content: '""',
      display: 'block',
      paddingTop: '56.25%',
    },
  },
  stepperRoot: {
    bottom: 0,
    left: '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
    zIndex: 1,
  },
  stepperDot: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[50],
    backgroundColor: 'transparent',
    width: theme.spacing(1),
    height: theme.spacing(1),
    borderRadius: theme.spacing(1),

    '&:not(:last-of-type)': {
      marginRight: theme.spacing(1 / 2),
    },
  },
  stepperDotActive: {
    borderColor: theme.palette.grey[50],
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
  },
  carouselMediaContainer: {
    cursor: 'grab',
    position: 'relative',

    '&:hover > button': {
      opacity: 0.8,

      '&:hover': {
        opacity: 1,
      },
    },
  },
  media: {
    height: '100%',
    width: '100%',
    zIndex: -1,
  },
  staticMedia: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
}));

export const Media = forwardRef<HTMLImageElement, MediaProps>(({
  fallback, images, title,
  className = '',
  enableCarousel = false,
  sizeCallback = getImageMediumUrl,
  preserveRatio = true,
  loading = 'lazy',
}, ref) => {
  const { classes, cx } = useStyles();

  const [imageIndex, setImageIndex] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    watchDrag: true,
    watchResize: true,
    watchSlides: false,
  }, [WheelGesturesPlugin()]);

  const handlePrevButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const handleNextButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi) {
      const onSelect = () => {
        setImageIndex(emblaApi.selectedScrollSnap());
      };

      onSelect();

      emblaApi.on('select', onSelect);
      emblaApi.on('reInit', onSelect);
    }
  }, [emblaApi, setImageIndex]);

  const imageArray = getImageArrayFromField(images, fallback);

  if (enableCarousel && imageArray.length > 1) {
    return (
      <div className={cx(classes.root, classes.carouselMediaContainer, className)}>
        <div
          ref={emblaRef}
          className={classes.embla}
        >
          <div className={cx(classes.emblaContainer, preserveRatio && classes.preserveRatio)}>
            {map(imageArray, (image, index) => (
              <ImageLoader
                {...image}
                sizeCallback={sizeCallback}
                key={image.path}
              >
                {(src) => (
                  <CardMedia
                    alt={title}
                    className={cx(classes.emblaSlide, classes.media)}
                    component="img"
                    loading={loading}
                    src={src}
                    ref={ref}
                    title={title}
                    aria-hidden={index !== imageIndex}
                  />
                )}
              </ImageLoader>
            ))}
          </div>
        </div>

        <ArrowButton
          direction="left"
          onClick={handlePrevButtonClick}
          title="previous image"
        />

        <ArrowButton
          direction="right"
          onClick={handleNextButtonClick}
          title="next image"
        />

        <MobileStepper
          variant="dots"
          steps={images.length}
          position="static"
          activeStep={imageIndex}
          backButton={null}
          nextButton={null}
          classes={{
            root: classes.stepperRoot,
            dot: classes.stepperDot,
            dotActive: classes.stepperDotActive,
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={cx(
        classes.root,
        preserveRatio && classes.preserveRatio,
        className,
      )}
    >
      <ImageLoader
        {...imageArray[0]}
        sizeCallback={sizeCallback}
      >
        {(src) => (
          <CardMedia
            alt={title}
            className={cx(classes.media, classes.staticMedia)}
            component="img"
            loading={loading}
            src={src}
            ref={ref}
            title={title}
          />
        )}
      </ImageLoader>
    </div>
  );
});
